import { ChevronDown, Pencil, Target, Trash2 } from 'lucide-react'
import { useState } from 'react'

import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { useBookAiPurposeStore, useCurrentPurpose } from '@/store/useBookAiPurposeStore'

import { PurposeEditor } from './purpose-editor'

/** シートのヘッダーに出す、この本を読む目的（未設定なら「目的を決める」）。押すと目的の編集を開く */
export const PurposeChip = ({
  episodeId,
  open,
  onToggle,
}: {
  episodeId: number
  open: boolean
  onToggle: () => void
}) => {
  const isLoaded = useBookAiPurposeStore((state) => state.episodeId === episodeId && state.isLoaded)
  const purpose = useCurrentPurpose(episodeId)?.purpose
  if (!isLoaded) return null
  return (
    <button
      type="button"
      aria-expanded={open}
      onClick={onToggle}
      className={cn(
        'flex max-w-full min-w-0 items-center gap-1.5 rounded-full border px-3 py-1 text-xs',
        purpose
          ? 'border-purple-500/30 bg-purple-500/10 text-foreground'
          : 'border-dashed border-purple-500/50 text-purple-600 dark:text-purple-300',
      )}
    >
      <Target className="size-3.5 shrink-0 text-purple-500" />
      <span className="truncate">{purpose ?? 'この本を読む目的を決める'}</span>
      <ChevronDown className={cn('size-3.5 shrink-0 transition-transform', open && 'rotate-180')} />
    </button>
  )
}

/** AI と磨く前に最初に書いた目的と、磨くときに答えた状況（磨いていなければ何も出さない） */
export const PurposeOrigin = ({ episodeId }: { episodeId: number }) => {
  const purpose = useCurrentPurpose(episodeId)
  if (!purpose || (!purpose.original_purpose && purpose.context.length === 0)) return null
  return (
    <div className="space-y-0.5 text-[11px] text-muted-foreground">
      {purpose.original_purpose && <p className="wrap-break-word">最初に書いた目的: {purpose.original_purpose}</p>}
      {purpose.context.length > 0 && (
        <p className="wrap-break-word">あなたの状況: {purpose.context.map((item) => item.answer).join(' / ')}</p>
      )}
    </div>
  )
}

/** 目的の編集（ヘッダーの目的を押したときにシートの上部に出す） */
export const PurposePanel = ({ episodeId, onClose }: { episodeId: number; onClose: () => void }) => {
  const purpose = useCurrentPurpose(episodeId)?.purpose
  const remove = useBookAiPurposeStore((state) => state.remove)
  const [confirming, setConfirming] = useState(false)

  return (
    <div className="space-y-2 rounded-xl border border-purple-500/30 bg-purple-500/5 p-3">
      <p className="flex items-center gap-1.5 text-sm font-semibold">
        {purpose ? <Pencil className="size-4 text-purple-500" /> : <Target className="size-4 text-purple-500" />}
        {purpose ? '読む目的を書き換える' : 'この本を読む目的は？'}
      </p>
      <p className="text-xs text-muted-foreground">
        目的に効く章と箇所・読み方の提案・目的に合わせた答えと問いを出します。「AI
        と磨く」で、この本をうまく使える目的に一緒に磨けます。書き換えると提案と実践メモは作り直しになります
      </p>
      <PurposeOrigin episodeId={episodeId} />
      <PurposeEditor key={purpose ?? ''} initial={purpose ?? ''} onSaved={onClose} onCancel={onClose} />
      {purpose && (
        <div className="flex items-center justify-end gap-1 border-t pt-2">
          {confirming ? (
            <>
              <span className="text-xs text-muted-foreground">提案・実践メモも消えます</span>
              <Button variant="ghost" size="sm" className="px-2!" onClick={() => setConfirming(false)}>
                やめる
              </Button>
              <Button
                variant="destructive"
                size="sm"
                className="px-2!"
                onClick={async () => {
                  await remove()
                  onClose()
                }}
              >
                消す
              </Button>
            </>
          ) : (
            <Button variant="ghost" size="sm" className="px-2! text-xs" onClick={() => setConfirming(true)}>
              <Trash2 className="size-3.5" />
              目的を消す
            </Button>
          )}
        </div>
      )}
    </div>
  )
}
